import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getInvoiceSummary } from '../../../services/dashboardService';
import { InvoiceSummaryData } from '../../../types/dashboard';
import { useAuth } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';

interface Branch {
  _id: string;
  name: string;
}

const InvoiceSummaryWidget: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<InvoiceSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [branches, setBranches] = useState<Branch[]>([]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchData();
  }, [period, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await apiService.get<Branch[]>('/users/branches');
      setBranches(response);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch === 'all' ? undefined : selectedBranch;
      const result = await getInvoiceSummary(period, branchId);
      setData(result);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل بيانات ملخص الفواتير');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    setPeriod(event.target.value as 'day' | 'week' | 'month');
  };

  const handleBranchChange = (event: SelectChangeEvent<string>) => {
    setSelectedBranch(event.target.value);
  };

  const handleViewDetails = () => {
    navigate('/invoices/reports/summary');
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('ar-SA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'day':
        return 'اليوم';
      case 'week':
        return 'هذا الأسبوع';
      case 'month':
        return 'هذا الشهر';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 400 }}>
        <Typography color="error">{error || 'لا توجد بيانات'}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #667eea',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with Filters */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                mr: 2
              }}
            >
              📊
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                ملخص الفواتير
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6' }}>
                المبيعات والمردودات - {getPeriodLabel()}
              </Typography>
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={period}
                  onChange={handlePeriodChange}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                >
                  <MenuItem value="day">يوم</MenuItem>
                  <MenuItem value="week">أسبوع</MenuItem>
                  <MenuItem value="month">شهر</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedBranch}
                  onChange={handleBranchChange}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    }
                  }}
                >
                  <MenuItem value="all">جميع الفروع</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Sales Card */}
          <Grid item xs={4}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                p: { xs: 1, sm: 2 },
                borderRadius: 2,
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(17, 153, 142, 0.4)'
                }
              }}
            >
              <TrendingUpIcon sx={{ fontSize: { xs: 24, sm: 32 }, mb: 0.5, opacity: 0.9 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  mb: 0.5,
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                  wordBreak: 'break-word',
                  lineHeight: 1.2
                }}
              >
                {formatCurrency(data.sales)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                المبيعات
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                ({data.salesCount} فاتورة)
              </Typography>
            </Box>
          </Grid>

          {/* Returns Card */}
          <Grid item xs={4}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                p: { xs: 1, sm: 2 },
                borderRadius: 2,
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(235, 51, 73, 0.4)'
                }
              }}
            >
              <TrendingDownIcon sx={{ fontSize: { xs: 24, sm: 32 }, mb: 0.5, opacity: 0.9 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  mb: 0.5,
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                  wordBreak: 'break-word',
                  lineHeight: 1.2
                }}
              >
                {formatCurrency(data.returns)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                المردودات
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                ({data.returnsCount} فاتورة)
              </Typography>
            </Box>
          </Grid>

          {/* Net Sales Card */}
          <Grid item xs={4}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: { xs: 1, sm: 2 },
                borderRadius: 2,
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                }
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: { xs: 24, sm: 32 }, mb: 0.5, opacity: 0.9 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  mb: 0.5,
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                  wordBreak: 'break-word',
                  lineHeight: 1.2
                }}
              >
                {formatCurrency(data.netSales)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                صافي المبيعات
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                (بعد خصم المردودات)
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Chart */}
        <Box sx={{ mb: 2, backgroundColor: '#f8f9fa', p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
            الرسم البياني
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#7f8c8d', fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis
                tick={{ fill: '#7f8c8d', fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 12
                }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                iconType="circle"
              />
              <Bar dataKey="sales" fill="#11998e" name="المبيعات" radius={[8, 8, 0, 0]} />
              <Bar dataKey="returns" fill="#eb3349" name="المردودات" radius={[8, 8, 0, 0]} />
              <Bar dataKey="net" fill="#667eea" name="الصافي" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* View Details Button */}
        <Button
          fullWidth
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleViewDetails}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 'bold',
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              transform: 'scale(1.02)'
            }
          }}
        >
          عرض التفاصيل الكاملة
        </Button>
      </CardContent>
    </Card>
  );
};

export default InvoiceSummaryWidget;